import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import LinkIcon from "@mui/icons-material/Link"
import LinkOffIcon from "@mui/icons-material/LinkOff"
import NoteAddIcon from "@mui/icons-material/NoteAdd"
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    MenuItem,
    Select,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs from "dayjs"
import { useState } from "react"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { IOrderOption } from "@/components/OrderPickerDialog"
import OrderPickerDialog from "@/components/OrderPickerDialog"
import NewOrderDialog, { type IQuickCreatedOrder } from "./NewOrderDialog"

export interface IExportEntryRow {
    id?: number
    order_id: number | null
    op_date: string | null
    reference_number: number | null
    project_name: string
    paper_color: number
    printing_inks_amount: number
    kilograms: number
    art_request: number
    plate_request: number
    plate_approval: number
    export_number: string | null
    exit_date: string | null
    urgent: boolean
    order?: {
        id: number
        order_code: string
    } | null
    _destroy?: boolean
}

interface Props {
    index: number
    parentClientName: string
    excludedOrderIds: number[]
    onUpdate: (patch: Partial<IExportEntryRow>) => void
    onRemove: () => void
}

export default function EntryRow({
    index, parentClientName, excludedOrderIds, onUpdate, onRemove,
}: Props) {
    const { t } = useTranslation()
    const { control, watch } = useFormContext()
    const [pickerOpen, setPickerOpen] = useState(false)
    const [createOpen, setCreateOpen] = useState(false)
    const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false)

    const orderId = watch(`export_entries.${index}.order_id`) as number | null
    const orderCode = watch(`export_entries.${index}.order.order_code`) as string | undefined
    const urgent = watch(`export_entries.${index}.urgent`) as boolean
    const kilograms = watch(`export_entries.${index}.kilograms`) as number | null
    const referenceNumber = watch(`export_entries.${index}.reference_number`) as number | null

    const linked = orderId != null

    const applyOrderToRow = (order: {
        id: number; order_code: string; project_name: string; reference_paper_color: number;
        printing_inks_amount: number; general_info_programmed_kilograms: number;
        // Optional: when linking/bulk-adding an existing order we also seed the
        // export-specific OP Date and Order (reference number) columns from the
        // order. reference_number gets the order's highest reference. Omitted by
        // the quick-create flow, which keeps the draft values the user already
        // typed into the row.
        order_entry_date?: string | null; reference_number?: number | null;
    }) => {
        const patch: Partial<IExportEntryRow> = {
            order_id: order.id,
            order: { id: order.id, order_code: order.order_code },
            project_name: order.project_name ?? "",
            paper_color: order.reference_paper_color ?? 0,
            printing_inks_amount: order.printing_inks_amount ?? 0,
            kilograms: order.general_info_programmed_kilograms ?? 0,
        }
        if (order.order_entry_date !== undefined) patch.op_date = order.order_entry_date
        if (order.reference_number !== undefined) patch.reference_number = order.reference_number
        onUpdate(patch)
    }

    const handleLink = (orders: IOrderOption[]) => {
        const order = orders[0]
        if (!order) return
        applyOrderToRow({
            id: order.id,
            order_code: order.order_code,
            project_name: order.project_name,
            reference_paper_color: order.reference_paper_color ?? 0,
            printing_inks_amount: order.printing_inks_amount ?? 0,
            general_info_programmed_kilograms: order.general_info_programmed_kilograms ?? 0,
            order_entry_date: order.order_entry_date ?? null,
            reference_number: order.max_reference_number ?? null,
        })
        setPickerOpen(false)
    }

    const handleCreated = (order: IQuickCreatedOrder) => applyOrderToRow(order)

    const confirmUnlink = () => {
        onUpdate({ order_id: null, order: null })
        setUnlinkConfirmOpen(false)
    }

    const rowSx = {
        borderLeft: 4,
        borderColor: urgent ? "error.main" : "transparent",
        // Compact rows: reduce vertical padding inside cells.
        "& > td": { py: 0.5, px: 0.75 },
    }

    const numericCell = (name: keyof IExportEntryRow) => (
        <Controller
            control={control}
            name={`export_entries.${index}.${String(name)}`}
            render={({ field }) => (
                <TextField
                    {...field}
                    type="number"
                    size="small"
                    value={field.value ?? ""}
                    onChange={e => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                    sx={{ width: 85 }}
                />
            )}
        />
    )

    const dateCell = (name: "op_date" | "exit_date") => (
        <Controller
            control={control}
            name={`export_entries.${index}.${name}`}
            render={({ field }) => (
                <DatePicker
                    value={field.value ? dayjs(field.value as string) : null}
                    onChange={v => field.onChange(v?.format("YYYY-MM-DD") ?? null)}
                    format="DD-MM-YYYY"
                    slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
                />
            )}
        />
    )

    const tristateCell = (name: "art_request" | "plate_request" | "plate_approval") => (
        <Controller
            control={control}
            name={`export_entries.${index}.${name}`}
            render={({ field }) => (
                <Select
                    {...field}
                    size="small"
                    value={field.value ?? 0}
                    onChange={e => field.onChange(Number(e.target.value))}
                    sx={{ width: 90 }}
                >
                    <MenuItem value={0}>{t("export.form.tristate.na")}</MenuItem>
                    <MenuItem value={1}>{t("export.form.tristate.yes")}</MenuItem>
                    <MenuItem value={2}>{t("export.form.tristate.no")}</MenuItem>
                </Select>
            )}
        />
    )

    return (
        <>
            <TableRow hover sx={rowSx}>
                <TableCell sx={{ whiteSpace: "nowrap" }}>{dateCell("op_date")}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {linked ? (
                        <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {referenceNumber ?? "—"}
                        </Typography>
                    ) : numericCell("reference_number")}
                </TableCell>
                <TableCell>
                    <Controller
                        control={control}
                        name={`export_entries.${index}.project_name`}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                size="small"
                                value={field.value ?? ""}
                                sx={{ width: 220 }}
                            />
                        )}
                    />
                </TableCell>
                <TableCell>
                    <Controller
                        control={control}
                        name={`export_entries.${index}.paper_color`}
                        render={({ field }) => (
                            <Select
                                {...field}
                                size="small"
                                value={field.value ?? 0}
                                onChange={e => field.onChange(Number(e.target.value))}
                                sx={{ width: 110 }}
                            >
                                <MenuItem value={0}>{t("export.form.color.na")}</MenuItem>
                                <MenuItem value={1}>{t("export.form.color.white")}</MenuItem>
                                <MenuItem value={2}>{t("export.form.color.kraft")}</MenuItem>
                            </Select>
                        )}
                    />
                </TableCell>
                <TableCell>
                    <Controller
                        control={control}
                        name={`export_entries.${index}.printing_inks_amount`}
                        render={({ field }) => (
                            <Select
                                {...field}
                                size="small"
                                value={field.value ?? 0}
                                onChange={e => field.onChange(Number(e.target.value))}
                                sx={{ width: 130 }}
                            >
                                <MenuItem value={0}>{t("orders.form.ink.noInk")}</MenuItem>
                                <MenuItem value={1}>{t("orders.form.ink.oneInk")}</MenuItem>
                                <MenuItem value={2}>{t("orders.form.ink.twoInks")}</MenuItem>
                                <MenuItem value={3}>{t("orders.form.ink.threeInks")}</MenuItem>
                                <MenuItem value={4}>{t("orders.form.ink.fourInks")}</MenuItem>
                            </Select>
                        )}
                    />
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap", minWidth: 90 }}>
                    <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {kilograms != null
                            ? Number(kilograms).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : "—"}
                    </Typography>
                </TableCell>
                <TableCell>{tristateCell("art_request")}</TableCell>
                <TableCell>{tristateCell("plate_request")}</TableCell>
                <TableCell>{tristateCell("plate_approval")}</TableCell>
                <TableCell>
                    <Controller
                        control={control}
                        name={`export_entries.${index}.export_number`}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                size="small"
                                value={field.value ?? ""}
                                sx={{ width: 85 }}
                            />
                        )}
                    />
                </TableCell>
                <TableCell>{dateCell("exit_date")}</TableCell>
                <TableCell padding="checkbox" align="center">
                    <Controller
                        control={control}
                        name={`export_entries.${index}.urgent`}
                        render={({ field }) => (
                            <Checkbox
                                size="small"
                                color="error"
                                checked={!!field.value}
                                onChange={e => field.onChange(e.target.checked)}
                            />
                        )}
                    />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", width: "1%", px: 0.25 }}>
                    {linked ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title={t("export.form.entry.action.openOrder")}>
                                <Chip
                                    size="small"
                                    color="success"
                                    label={orderCode ?? `#${orderId}`}
                                    component="a"
                                    href={`/orders/${orderId}/edit`}
                                    target="_blank"
                                    clickable
                                />
                            </Tooltip>
                            <Tooltip title={t("export.form.entry.action.unlink")}>
                                <IconButton size="small" color="warning" onClick={() => setUnlinkConfirmOpen(true)}>
                                    <LinkOffIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title={t("export.form.entry.action.link")}>
                                <IconButton size="small" color="primary" onClick={() => setPickerOpen(true)}>
                                    <LinkIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={t("export.form.entry.action.create")}>
                                <IconButton size="small" color="success" onClick={() => setCreateOpen(true)}>
                                    <NoteAddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </TableCell>
                <TableCell padding="checkbox" align="center">
                    <Tooltip title={t("common.remove")}>
                        <IconButton size="small" color="error" onClick={onRemove}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>

            <Dialog
                open={unlinkConfirmOpen}
                onClose={() => setUnlinkConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{t("export.form.entry.unlinkConfirmTitle")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("export.form.entry.unlinkConfirmText", { code: orderCode ?? `#${orderId}` })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUnlinkConfirmOpen(false)} color="inherit" size="small">
                        {t("common.cancel")}
                    </Button>
                    <Button onClick={confirmUnlink} color="warning" variant="contained" size="small">
                        {t("export.form.entry.action.unlink")}
                    </Button>
                </DialogActions>
            </Dialog>

            <OrderPickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onConfirm={handleLink}
                excludedOrderIds={excludedOrderIds}
            />
            <NewOrderDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                parentClientName={parentClientName}
                entryDraft={{
                    project_name: watch(`export_entries.${index}.project_name`) ?? "",
                    paper_color: watch(`export_entries.${index}.paper_color`) ?? 0,
                    printing_inks_amount: watch(`export_entries.${index}.printing_inks_amount`) ?? 0,
                    kilograms: watch(`export_entries.${index}.kilograms`) ?? 0,
                    reference_number: watch(`export_entries.${index}.reference_number`) ?? null,
                    op_date: watch(`export_entries.${index}.op_date`) ?? null,
                }}
                onCreated={handleCreated}
            />
        </>
    )
}
